import { useEffect } from 'react';
import { useLatest } from './useLatest';

/**
 * Call `callback` every `delayMs` while mounted; a null delay pauses it. The callback always sees the
 * latest props and state, and changing it does not restart the interval; changing the delay does.
 */
export function useInterval(callback: () => void, delayMs: number | null): void {
    const latest = useLatest(callback);
    useEffect(() => {
        if (delayMs === null) return undefined;
        const timer = window.setInterval(() => latest.current(), delayMs);
        return () => window.clearInterval(timer);
    }, [delayMs, latest]);
}

/**
 * Call `callback` once, `delayMs` after the delay is set (or changes), unless the component unmounts or
 * the delay becomes null first. Use it for a deferred UI step (clear a "Copied" label, focus after a
 * dialog opens); restart it by changing `delayMs` or passing a `restartKey`.
 */
export function useTimeout(callback: () => void, delayMs: number | null, restartKey?: unknown): void {
    const latest = useLatest(callback);
    useEffect(() => {
        if (delayMs === null) return undefined;
        const timer = window.setTimeout(() => latest.current(), delayMs);
        return () => window.clearTimeout(timer);
    }, [delayMs, restartKey, latest]);
}
